import {
  CartesianGrid,
  Label,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useImmer } from "use-immer";

import { TimeSeriesColors } from "@intelligems/ig-graphs";
import { IgTypography } from "@intelligems/ig-theme";
import { GraphConfig, LineData } from "@intelligems/ig-types";
import { SxProps } from "@mui/material";
import { Stack } from "@mui/system";
import { AxisTick } from "@src/features/analytics/baseDashboard/chartComponents/AxisTick";
import { useGraphValueFormatter } from "@src/features/analytics/hooks/use-graph-value-formatter";
import { InteractiveChartLegend } from "@src/features/analytics/legends/InteractiveChartLegend";
import { theme } from "@src/theme";
import { LineMetricData } from "@src/types/analytics";

import { useGetTooltipProps } from "../../hooks/use-get-tooltip-props";
interface IgLineChartProps {
  config: GraphConfig;
  groupMetricData: LineMetricData[];
  externalFocusGroups?: string[];
  syncId?: string;
  legends: LineData[];
  dateFormat?: string;
  graphHeight?: number;
}

export const ProfitAndRevenueChart = ({
  config,
  groupMetricData,
  externalFocusGroups,
  syncId,
  legends,
  dateFormat,
  graphHeight,
}: IgLineChartProps) => {
  const graphValueFormatter = useGraphValueFormatter();
  const [baseFocusGroups, setBaseFocusGroups] = useImmer<string[]>([]);
  const tooltipProps = useGetTooltipProps({
    legends: legends.filter((line) => !baseFocusGroups.includes(line.id)),
    dateFormat,
  });

  const handleLegendClick = (dataKey: string) => {
    if (baseFocusGroups.includes(dataKey)) {
      setBaseFocusGroups(baseFocusGroups.filter((el) => el !== dataKey));
    } else {
      setBaseFocusGroups((prev) => [...prev, dataKey]);
    }
  };

  return (
    <ResponsiveContainer width="100%" height={graphHeight ?? 300}>
      <LineChart
        syncId={syncId}
        data={groupMetricData}
        margin={{ top: 5, bottom: 5, left: -20 }}
      >
        <CartesianGrid
          style={{ stroke: theme.igBorders.core.disabled }}
          vertical={false}
        />
        <XAxis
          label={
            <Label
              style={{
                fill: theme.palette.text.primary,
                fontFamily: theme.typography.fontFamily,
                fontSize: 12,
              }}
              position={"insideBottom"}
              offset={-5}
              textAnchor={"middle"}
            />
          }
          padding={{ left: 30, right: 30 }}
          tick={(props) => (
            <AxisTick
              config={config}
              graphValueFormatter={graphValueFormatter}
              groupMetricData={groupMetricData}
              dateFormat={dateFormat}
              {...props}
            />
          )}
          axisLine={{ stroke: theme.igBorders.core.disabled }}
          tickLine={false}
          dataKey={"xAxisKey"}
          domain={["auto", "auto"]}
        />

        <YAxis
          axisLine={false}
          label={
            <Label
              style={{
                fill: theme.palette.text.primary,
                fontFamily: theme.typography.fontFamily,
                fontSize: 12,
                marginTop: "10px",
                textAnchor: "middle",
              }}
              angle={270}
              position={"insideLeft"}
              offset={10}
              textAnchor={"middle"}
            />
          }
          style={{ fontFamily: theme.typography.fontFamily, fontSize: 10 }}
          tickLine={false}
          tickFormatter={(value: any) =>
            graphValueFormatter({
              isAxisTick: true,
              value,
              configDataTypes: ["currencyNoDecimal"],
            })!
          }
          yAxisId="left"
        />

        <YAxis
          axisLine={false}
          domain={[0, 1]}
          label={
            <Label
              style={{
                fill: theme.palette.text.primary,
                fontFamily: theme.typography.fontFamily,
                fontSize: 12,
                marginTop: "10px",
                textAnchor: "middle",
              }}
              angle={270}
              position={"insideLeft"}
              offset={10}
              textAnchor={"middle"}
            />
          }
          orientation="right"
          style={{ fontFamily: theme.typography.fontFamily, fontSize: 10 }}
          tickLine={false}
          tickFormatter={(value: any) =>
            graphValueFormatter({
              isAxisTick: true,
              value: value,
              configDataTypes: ["percent"],
            })!
          }
          yAxisId="right"
        />

        <Tooltip {...tooltipProps} />

        {config.showLegend && legends.length > 1 && (
          <Legend
            content={
              <InteractiveChartLegend
                onLegendClick={handleLegendClick}
                focusGroups={baseFocusGroups}
                lines={legends}
              />
            }
            wrapperStyle={{ paddingTop: 24 }}
          />
        )}

        {legends.map(({ id }, index) => (
          <Line
            connectNulls={false}
            dataKey={id}
            dot={false}
            hide={
              baseFocusGroups.includes(id) ||
              (externalFocusGroups && externalFocusGroups.includes(id))
            }
            key={id}
            stroke={TimeSeriesColors[index]}
            strokeWidth={1.5}
            type="monotone"
            yAxisId={id === "gross_margin_pct" ? "right" : "left"}
          />
        ))}
      </LineChart>
    </ResponsiveContainer>
  );
};
